import React, { useState, useEffect } from 'react';
import {fetchPractitionerByGsdlNo, getNewOrderForPractLookup} from '../util/apiUtils';
import {useHistory} from 'react-router-dom';
import { ReCAPTCHA } from 'react-google-recaptcha';

function UKPractitionerLookup() {
    const history = useHistory();
    const [practitionerCode, setPractitionerCode] = useState('');
    const [practitioner, setPractitioner] = useState(null);
    const [canOrder, setCanOrder] = useState(false);
    const [notFoundPractitioner, setNotFoundPractitioner] = useState(false);
    const grecaptchaSiteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITEKEY;
    const [grecaptchaObject, setGrecaptchaObject] = useState(window.grecaptchaObject);
    const [captchaValue, setCaptchaValue] = useState(null);
    const [scriptCount, setScriptCount] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleInputChange = (e) => {
        setPractitionerCode(e.target.value);
    };

    const resetReCaptcha = () => {
        if (grecaptchaObject && grecaptchaObject.reset) {
            grecaptchaObject.reset();
        }
    };
    
    /**
     * Attempt to store the window.grecaptcha value in state.
     * If the value is not available, try again after 500 ms
     */
    const loadGrecaptchaObject = () => {
        // Setting grecaptchaObject doesn't always force a re-render because of how it is a reference to window.grecaptcha
        // The scriptCount value is to make sure state is refreshed and a re-render happens each time this method is called
        setScriptCount((prev) => prev + 1);
        const newObj = window.grecaptcha;

        if (!newObj || !newObj.reset) {
            setTimeout(() => {
                loadGrecaptchaObject();
            }, 500);
        } else {
            setGrecaptchaObject(newObj);
        }
    };

    /**
     * Load the Google ReCaptcha script, which will create the window.grecaptcha object
     */
    const loadRecaptchaScript = () => {
        const script = document.createElement('script');
        script.src = '//www.google.com/recaptcha/api.js';
        script.type = 'text/javascript';
        document.head.appendChild(script);
        loadGrecaptchaObject();
    };

    /**
     * Load the Google ReCaptcha script and object when the component mounts.
     */
    useEffect(() => {
        if (!window.grecaptcha) {
            loadRecaptchaScript();			
        } else {
            loadGrecaptchaObject();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleCaptchaChange = (value) => {
        // null is passed in when the recaptcha expires. Change that to 'false' so we know the recaptcha has been attempted.
        setCaptchaValue(value || false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setNotFoundPractitioner(false);
        fetchPractitionerByGsdlNo(practitionerCode).then(practitioner => {
            if(!practitioner) {
                setPractitioner(null);
                setCanOrder(false);               
                setCaptchaValue(null);
                setErrorMessage(null);
                setNotFoundPractitioner(true);
                return;
            }
            setNotFoundPractitioner(false);
            practitioner.nameCommaDegree = practitioner.firstName + ' ' + practitioner.lastName + (practitioner.degree ? ', ' + practitioner.degree : '');
            setPractitioner(practitioner);
            setCanOrder(practitioner && ![1, 2].includes(practitioner.cmsBillingTypeId) && practitioner.ukdropShipClient);
            setCaptchaValue(null);
            setErrorMessage(null);            
        }).catch(e => {
            console.error(`An unexpected error occurred fetching practitioner details for ${practitionerCode.toUpperCase()}.`);
            setPractitioner(null);
        });
    };

    const handleNext = (e) => {
        e.preventDefault();
        if (!captchaValue) {
			setCaptchaValue(false);
			return;
		}
       getNewOrderForPractLookup(practitioner.gsdlNo, captchaValue ? captchaValue : null).then(order => {            
			setCaptchaValue(null);
	        let nextStep = 'panels';
            history.push(`/order/${order.id}/${nextStep}`);
        }).catch(e => {
            console.error('An error occurred creating a new order for practitioner.', e);
            setCaptchaValue(null);            
            resetReCaptcha();
            if (e.response) {
                return e.response.text().then((text) => {
                    if (text) {
                        let json = JSON.parse(text);
                        if (json.error && json.error === 'RecaptchaInvalidException') {
                            setErrorMessage('Error verifying reCAPTCHA. Please try again.');
                            return Promise.resolve();
                        } else if (json.code && json.code === 'recaptcha-invalid') {
                            setErrorMessage('Error verifying reCAPTCHA. Please try again.');
                            return Promise.resolve();
                        }
                    }
                    return Promise.reject('Unexpected error');
                });
            } else {
                console.error(`An unexpected error occurred creating a new order for practitioner: ${practitioner.gsdlNo}.`, e);
                return Promise.reject('Unexpected error');
            }          
        }).finally(() => {
				resetReCaptcha();				
		});
    };

    return (
        <div className="banner section">
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <h1 className="page-title margin-top-clear">Practitioner Lookup</h1>
                    </div>
                </div>
                <div className='ps-3 pe-3 pb-3 pt-0'>
                    <div className='alert alert-info'>
                        Please enter your practitioner's five digit account number so that we may process your order.
                        It is not possible to place an order without this number.  If you do not have your
                        practitioner's account number, please contact them directly, or call us to verify it, on 020 8336 7750.
                    </div>
                    <div>
                        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="practitioner-code">Practitioner Code</label>
                            <div className='col-2'>
                                <input
                                    id="practitioner-code"
                                    type="text"
                                    className="form-control"
                                    value={practitionerCode}
                                    onChange={handleInputChange}
                                    style={{ textTransform: 'uppercase' }}
                                    maxLength="5"
                                />
                            </div>
                            <button className="btn btn-white btn-sm" type="submit">Search</button>
                        </form>
                    </div>
                    {practitioner &&
                        (<div>
                            <form className="mt-3 alert alert-warning" onSubmit={handleNext}>
                                <h2>Practitioner Details</h2>
                                <div>{practitioner?.nameCommaDegree || ''}</div>
                                {practitioner?.nameCommaDegree !== practitioner?.office?.name && (<div>{practitioner.office?.name}</div>)}
                                {practitioner?.office?.address?.address1 && (<div>{practitioner.office.address.address1}</div>)}
                                {practitioner?.office?.address?.address2 && (<div>{practitioner.office.address.address2}</div>)}
                                {practitioner?.office?.address?.address3 && (<div>{practitioner.office.address.address3}</div>)}
                                {practitioner?.office?.address?.city && (<div>{practitioner.office.address.city}{practitioner.office.address.state ? ', ' : ''}</div>)}
                                {practitioner?.office?.address?.state && (<div>{practitioner.office.address.state}</div>)}
                                {practitioner?.office?.address?.zipCode && (<div>{practitioner.office.address.zipCode}</div>)}
                                {
                                    !canOrder ? <div className="text-danger"><br />We're sorry, but your healthcare provider is not currently set up for online test ordering. To place an order, please contact your provider directly.</div>
                                        : <div><br />If this is not your practitioner, please enter the correct practitioner code and click Search. <br /> Please select as appropriate below and click Next to continue.</div>
                                }
                                {canOrder && (
                                    <div className="mt-3 gear-recaptcha col-6">
                                        <ReCAPTCHA
                                            grecaptcha={grecaptchaObject}
                                            sitekey={grecaptchaSiteKey}
                                            onChange={handleCaptchaChange}
                                            scriptcount={scriptCount}
                                            style={{ display: 'inline-block', outline: captchaValue === false ? '1px solid red' : '' }}
                                        />
                                    </div>
                                )}
                                {errorMessage && (
				                    <div className="alert alert-danger">{errorMessage}</div>
			            )}
                                <button className="btn btn-primary mt-2 float-end"
                                    type="submit"
                                    disabled={!canOrder || !captchaValue}>Next</button>
                            </form>
                        </div>
                        )}
                    {notFoundPractitioner &&
                        (<div className="mt-3 alert alert-danger">
                            Practitioner not found or is invalid. Please check the code and try again.
                        </div>
                        )}
                </div>
            </div>
        </div>
    );
}

export default UKPractitionerLookup;